import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { EventInput } from '@fullcalendar/core';
import { EventClickArg, DateSelectArg } from '@fullcalendar/core';
import { EventDropArg, DatesSetArg } from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';

import { CalendarViewComponent } from '../calendar-view/calendar-view.component';
import { CalendarEventDialogComponent } from '../calendar-event-dialog/calendar-event-dialog.component';
import { CalendarEventDetailDialogComponent } from '../calendar-event-detail-dialog/calendar-event-detail-dialog.component';
import { CalendarVisibilityDialogComponent } from '../calendar-visibility-dialog/calendar-visibility-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/components/confirm-dialog/confirm-dialog';
import { CalendarService } from '../../../services/calendar.service';
import { CalendarWebSocketService } from '../../../services/calendar-websocket.service';
import { ReminderService } from '../../../services/reminder.service';
import { NotificationPollingService } from '../../../services/notification-polling.service';
import { AuthService } from '../../../services/auth.service';
import { UserPreferencesService } from '../../../services/user-preferences.service';
import { CallCenterApiService } from '../../../services/call-center-api.service';
import { CallCenterDialogService } from '../../../services/call-center-dialog.service';
import { CallCenterBusinessService } from '../../../services/call-center-business.service';
import {
  CalendarEvent,
  CalendarEventCreate,
  CalendarEventUpdate,
  CalendarVisibleUser,
} from '../../../interfaces/calendar-event.interface';

/** Szín paletták a different userek naptárjaihoz */
const USER_COLORS = [
  '#7B1FA2', '#00796B', '#E64A19', '#5D4037',
  '#455A64', '#C2185B', '#1976D2', '#388E3C',
];

@Component({
  selector: 'app-calendar-wrapper',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatMenuModule,
    MatRadioModule,
    CalendarViewComponent
],
  templateUrl: './calendar-wrapper.component.html',
  styleUrls: ['./calendar-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarWrapperComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private wsReload$ = new Subject<void>();

  // Állapot
  isLoading = false;
  isFirstLoad = true;

  // Dátumtartomány (FullCalendar datesSet-ből)
  currentStart = '';
  currentEnd = '';

  // Saját események
  myEvents: CalendarEvent[] = [];

  // Más userek eseményei
  sharedEvents: CalendarEvent[] = [];

  // FullCalendar-nak átadott események
  calendarEvents: EventInput[] = [];

  // Szűrők
  showAppointments = true;
  showReminders = true;

  // Időrács beállítás
  slotDuration = '00:15:00';

  // Látható userek (calendar_visibility alapján)
  visibleUsers: CalendarVisibleUser[] = [];
  selectedUserIds: Set<number> = new Set();

  // Aktuális user
  currentUserId = 0;
  isManagerOrAdmin = false;

  constructor(
    private calendarService: CalendarService,
    private calendarWebSocketService: CalendarWebSocketService,
    private reminderService: ReminderService,
    private notificationPollingService: NotificationPollingService,
    private authService: AuthService,
    private userPreferencesService: UserPreferencesService,
    private callCenterApiService: CallCenterApiService,
    private callCenterDialogService: CallCenterDialogService,
    private businessService: CallCenterBusinessService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.currentUserId = user.id;
    }
    this.isManagerOrAdmin = this.authService.isAdmin() || this.authService.isManager();
    this.loadVisibleUsers();
    this.restoreSlotDuration();

    // WebSocket csatlakozás a valós idejű frissítéshez
    this.calendarWebSocketService.connect();
    this.calendarWebSocketService.onCalendarUpdate()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.wsReload$.next());

    // Debounce: ha rövid időn belül több WS értesítés érkezik, csak egyszer töltsük újra
    this.wsReload$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.loadEvents());
  }

  ngOnDestroy(): void {
    this.calendarWebSocketService.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Adatok betöltése ─────────────────────────────────

  onDatesSet(info: DatesSetArg): void {
    this.currentStart = info.startStr;
    this.currentEnd = info.endStr;
    this.loadEvents();
  }

  private loadVisibleUsers(): void {
    this.calendarService.getVisibleUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.visibleUsers = res.users || [];
          this.restoreCalendarSelection();
        },
        error: (err) => console.error('Látható felhasználók betöltése sikertelen:', err),
      });
  }

  loadEvents(): void {
    if (!this.currentStart || !this.currentEnd) return;
    this.isLoading = true;

    // Saját események
    this.calendarService.getEvents(this.currentStart, this.currentEnd)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.myEvents = res.events || [];
          this.loadSharedEventsIfNeeded();
        },
        error: (err) => {
          console.error('Események betöltése sikertelen:', err);
          this.isLoading = false;
          this.isFirstLoad = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadSharedEventsIfNeeded(): void {
    const userIds = Array.from(this.selectedUserIds);
    if (userIds.length === 0) {
      this.sharedEvents = [];
      this.buildCalendarEvents();
      return;
    }

    this.calendarService.getSharedEvents(this.currentStart, this.currentEnd, userIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.sharedEvents = res.events || [];
          this.buildCalendarEvents();
        },
        error: (err) => {
          console.error('Megosztott események betöltése sikertelen:', err);
          this.sharedEvents = [];
          this.buildCalendarEvents();
        },
      });
  }

  private buildCalendarEvents(): void {
    const events: EventInput[] = [];

    // Saját események
    for (const ev of this.myEvents) {
      if (!this.passesFilter(ev)) continue;
      events.push(this.toFullCalendarEvent(ev));
    }

    // Megosztott események
    for (const ev of this.sharedEvents) {
      if (!this.passesFilter(ev, true)) continue;
      const fc = this.toFullCalendarEvent(ev);
      // User-szín hozzárendelés
      const userIndex = this.visibleUsers.findIndex(u => u.user_id === ev.user_id);
      if (userIndex >= 0) {
        fc.color = USER_COLORS[userIndex % USER_COLORS.length];
        fc.textColor = '#fff';
      }
      fc.editable = ev.editable;
      events.push(fc);
    }

    this.calendarEvents = events;
    this.isLoading = false;
    this.isFirstLoad = false;
    this.cdr.markForCheck();
  }

  private passesFilter(ev: CalendarEvent, isShared = false): boolean {
    if (ev.source === 'reminder' && !this.showReminders) return false;
    // Az Időpontok szűrő csak a saját eseményekre vonatkozik, más felhasználók eseményeit nem érinti
    if (!isShared && ev.source === 'calendar_event' && !this.showAppointments) return false;
    return true;
  }

  private toFullCalendarEvent(ev: CalendarEvent): EventInput {
    let end = ev.end || undefined;
    // Reminder: fix időpont, de 15 perces magasságú megjelenítés a naptárban
    if (ev.source === 'reminder' && ev.start && !ev.allDay) {
      const startDate = new Date(ev.start);
      end = new Date(startDate.getTime() + 15 * 60 * 1000).toISOString();
    }
    // Saját reminder átmozgatható
    const isOwnReminder = ev.source === 'reminder' && ev.user_id === this.currentUserId;
    return {
      id: ev.id,
      title: ev.title,
      start: ev.start,
      end,
      allDay: ev.allDay,
      color: ev.color || (ev.source === 'reminder' ? '#FFC107' : '#1976D2'),
      textColor: ev.source === 'reminder' ? '#333' : '#fff',
      editable: ev.editable || isOwnReminder,
      durationEditable: ev.source !== 'reminder',
      extendedProps: { calendarEvent: ev },
    };
  }

  /** Lokális ISO formátum UTC konverzió nélkül (az adatbázis naive datetime-t tárol) */
  private toLocalISO(date: Date | null): string | undefined {
    if (!date) return undefined;
    const y = date.getFullYear();
    const mo = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    const h = date.getHours().toString().padStart(2, '0');
    const mi = date.getMinutes().toString().padStart(2, '0');
    return `${y}-${mo}-${d}T${h}:${mi}:00`;
  }

  /** Egész napos eseményekhez: csak dátum, időpont nélkül (00:00:00) */
  private toDateOnlyISO(date: Date): string {
    const y = date.getFullYear();
    const mo = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${mo}-${d}T00:00:00`;
  }

  // ─── Szűrők ─────────────────────────────────────────

  getUserColor(index: number): string {
    return USER_COLORS[index % USER_COLORS.length];
  }

  onFilterChange(): void {
    this.buildCalendarEvents();
  }

  onSlotDurationChange(): void {
    this.saveSlotDuration();
    this.cdr.markForCheck();
  }

  onUserToggle(userId: number, checked: boolean): void {
    if (checked) {
      this.selectedUserIds.add(userId);
    } else {
      this.selectedUserIds.delete(userId);
    }
    this.saveCalendarSelection();
    this.loadEvents();
  }

  // ─── FullCalendar események ─────────────────────────

  onDateSelect(info: DateSelectArg): void {
    const dialogRef = this.dialog.open(CalendarEventDialogComponent, {
      width: '500px',
      panelClass: 'crm-dialog',
      data: {
        mode: 'create' as const,
        startDate: info.start,
        endDate: info.end,
        allDay: info.allDay,
        visibleUsers: this.visibleUsers,
        currentUserId: this.currentUserId,
        slotDuration: this.slotDuration,
      },
    });

    dialogRef.afterClosed().subscribe((result: CalendarEventCreate | null) => {
      if (!result) return;
      this.calendarService.createEvent(result)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.snackBar.open('Esemény létrehozva', 'OK', { duration: 3000 });
            this.loadEvents();
          },
          error: () => this.snackBar.open('Hiba az esemény létrehozásakor', 'OK', { duration: 3000 }),
        });
    });
  }

  onEventClick(info: EventClickArg): void {
    const calEvent: CalendarEvent = info.event.extendedProps['calendarEvent'];
    if (!calEvent) return;

    const dialogRef = this.dialog.open(CalendarEventDetailDialogComponent, {
      width: '450px',
      panelClass: 'crm-dialog',
      data: { event: calEvent, currentUserId: this.currentUserId },
    });

    dialogRef.afterClosed().subscribe((result: { action: string; data?: any } | null) => {
      if (!result) return;

      if (result.action === 'edit') {
        this.openEditDialog(calEvent);
      } else if (result.action === 'delete') {
        this.deleteEvent(calEvent);
      } else if (result.action === 'statusChange') {
        this.updateEventStatus(calEvent, result.data);
      } else if (result.action === 'openCompanyStatus') {
        this.openCompanyStatusDialog(calEvent);
      }
    });
  }

  onEventDrop(info: EventDropArg): void {
    const calEvent: CalendarEvent = info.event.extendedProps['calendarEvent'];
    if (!calEvent) {
      info.revert();
      return;
    }

    const newStart = info.event.start;
    const isAllDay = info.event.allDay;
    const newStartLabel = newStart
      ? isAllDay
        ? newStart.toLocaleDateString('hu-HU')
        : newStart.toLocaleString('hu-HU')
      : '';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: calEvent.source === 'reminder' ? 'Emlékeztető áthelyezése' : 'Esemény áthelyezése',
        message: `Biztosan áthelyezed a(z) "${calEvent.title}" ${calEvent.source === 'reminder' ? 'emlékeztetőt' : 'eseményt'} ${newStartLabel}${isAllDay ? ' (egész napos)' : ''} időpontra?`,
        type: 'modify',
      } as ConfirmDialogData,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) {
        info.revert();
        return;
      }

      if (calEvent.source === 'reminder') {
        const newTimeISO = this.toLocalISO(newStart);
        if (!newTimeISO) {
          info.revert();
          return;
        }
        this.reminderService.updateReminderTime(calEvent.source_id, newTimeISO)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.snackBar.open('Emlékeztető áthelyezve', 'OK', { duration: 2000 });
              this.loadEvents();
            },
            error: () => {
              info.revert();
              this.snackBar.open('Hiba az áthelyezéskor', 'OK', { duration: 3000 });
            },
          });
      } else {
        // Egész napos mezőre húzáskor az időt nullázzuk, all_day flaget beállítjuk
        const startDate = info.event.start;
        const startISO = isAllDay && startDate
          ? this.toDateOnlyISO(startDate)
          : this.toLocalISO(startDate);

        // Ha egész napból időpontosba kerül az esemény (allDay → timed),
        // az end-et start + 1 óra alapján számoljuk, mert a FullCalendar
        // ilyenkor az eredeti nap-tartamból számítja az endet (pl. +24h)
        let endDate = info.event.end;
        const wasAllDay = calEvent.allDay;
        if (wasAllDay && !isAllDay && startDate) {
          endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        }

        const endISO = isAllDay && endDate
          ? this.toDateOnlyISO(endDate)
          : (endDate ? this.toLocalISO(endDate) : undefined);
        const update: CalendarEventUpdate = {
          start_time: startISO,
          end_time: endISO,
          all_day: isAllDay,
        };

        this.calendarService.updateEvent(calEvent.source_id, update)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.snackBar.open('Esemény áthelyezve', 'OK', { duration: 2000 }),
            error: () => {
              info.revert();
              this.snackBar.open('Hiba az áthelyezéskor', 'OK', { duration: 3000 });
            },
          });
      }
    });
  }

  onEventResize(info: EventResizeDoneArg): void {
    const calEvent: CalendarEvent = info.event.extendedProps['calendarEvent'];
    if (!calEvent || !calEvent.editable) {
      info.revert();
      return;
    }

    const update: CalendarEventUpdate = {
      start_time: this.toLocalISO(info.event.start),
      end_time: info.event.end ? this.toLocalISO(info.event.end) : undefined,
    };

    this.calendarService.updateEvent(calEvent.source_id, update)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.snackBar.open('Esemény átméretezve', 'OK', { duration: 2000 }),
        error: () => {
          info.revert();
          this.snackBar.open('Hiba az átméretezéskor', 'OK', { duration: 3000 });
        },
      });
  }

  // ─── Események kezelése ─────────────────────────────

  private openEditDialog(calEvent: CalendarEvent): void {
    const dialogRef = this.dialog.open(CalendarEventDialogComponent, {
      width: '500px',
      panelClass: 'crm-dialog',
      data: {
        mode: 'edit' as const,
        event: calEvent,
        visibleUsers: this.visibleUsers,
        currentUserId: this.currentUserId,
        slotDuration: this.slotDuration,
      },
    });

    dialogRef.afterClosed().subscribe((result: CalendarEventUpdate | null) => {
      if (!result) return;
      this.calendarService.updateEvent(calEvent.source_id, result)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.snackBar.open('Esemény módosítva', 'OK', { duration: 3000 });
            this.loadEvents();
          },
          error: () => this.snackBar.open('Hiba a módosításkor', 'OK', { duration: 3000 }),
        });
    });
  }

  private deleteEvent(calEvent: CalendarEvent): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: calEvent.source === 'reminder' ? 'Emlékeztető törlése' : 'Esemény törlése',
        message: `Biztosan törölni szeretnéd a(z) "${calEvent.title}" ${calEvent.source === 'reminder' ? 'emlékeztetőt' : 'eseményt'}?`,
        type: 'delete',
      } as ConfirmDialogData,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) return;

      const delete$ = calEvent.source === 'reminder'
        ? this.reminderService.deleteReminder(calEvent.source_id)
        : this.calendarService.deleteEvent(calEvent.source_id);

      delete$
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.snackBar.open(
              calEvent.source === 'reminder' ? 'Emlékeztető törölve' : 'Esemény törölve',
              'OK', { duration: 3000 }
            );
            if (calEvent.source === 'reminder') {
              this.notificationPollingService.refreshNow();
            }
            this.loadEvents();
          },
          error: () => this.snackBar.open('Hiba a törléskor', 'OK', { duration: 3000 }),
        });
    });
  }

  private updateEventStatus(calEvent: CalendarEvent, status: string): void {
    const update$ = calEvent.source === 'reminder'
      ? this.reminderService.updateReminderStatus(calEvent.source_id, status as any)
      : this.calendarService.updateEvent(calEvent.source_id, { status: status as any });

    update$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBar.open('Státusz módosítva', 'OK', { duration: 3000 });
          if (calEvent.source === 'reminder') {
            this.notificationPollingService.refreshNow();
          }
          this.loadEvents();
        },
        error: () => this.snackBar.open('Hiba a státusz módosításkor', 'OK', { duration: 3000 }),
      });
  }

  // ─── Láthatóság kezelés ─────────────────────────────

  private openCompanyStatusDialog(calEvent: CalendarEvent): void {
    if (!calEvent.company_id) return;

    this.callCenterApiService.reloadSingleElement(calEvent.company_id).subscribe({
      next: (companyData) => {
        this.callCenterApiService.getCompanyLogs(companyData.id).subscribe({
          next: (companyLogs) => {
            const statusOptions = this.businessService.state.statusOptions;
            this.callCenterDialogService.openStatusDialog(companyData, companyLogs, statusOptions)
              .subscribe((result) => {
                if (result && result.confirmed && (result.saveChangesOnly || result.selectedStatus)) {
                  this.businessService.processDialogResult(companyData, result);
                }
              });
          },
          error: () => {
            const statusOptions = this.businessService.state.statusOptions;
            this.callCenterDialogService.openStatusDialog(companyData, [], statusOptions)
              .subscribe((result) => {
                if (result && result.confirmed && (result.saveChangesOnly || result.selectedStatus)) {
                  this.businessService.processDialogResult(companyData, result);
                }
              });
          }
        });
      },
      error: () => {
        this.snackBar.open('Nem sikerült betölteni a cég adatait', 'Bezár', { duration: 3000 });
      }
    });
  }

  openVisibilityDialog(): void {
    const dialogRef = this.dialog.open(CalendarVisibilityDialogComponent, {
      width: '90vw',
      maxWidth: '95vw',
      maxHeight: '80vh',
      panelClass: 'crm-dialog',
    });

    dialogRef.afterClosed().subscribe((changed: boolean) => {
      if (changed) {
        this.loadVisibleUsers();
        this.loadEvents();
      }
    });
  }

  // ─── Naptár kiválasztás mentése/visszaállítása ──────

  private saveCalendarSelection(): void {
    const calendars = Array.from(this.selectedUserIds);
    this.userPreferencesService.saveCalendarSelection(calendars)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Naptár kiválasztás mentése sikertelen:', err),
      });
  }

  private restoreCalendarSelection(): void {
    this.userPreferencesService.getCalendarSelection()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const savedIds = res.calendars || [];
          const validUserIds = new Set(this.visibleUsers.map(u => u.user_id));
          this.selectedUserIds = new Set(savedIds.filter(id => validUserIds.has(id)));
          // Floating calendar szinkronizáció: értesítjük a streamet
          this.userPreferencesService.notifyCalendarSelectionLoaded(savedIds);
          this.cdr.markForCheck();
          if (this.selectedUserIds.size > 0 && this.currentStart && this.currentEnd) {
            this.loadEvents();
          }
        },
        error: (err) => {
          console.error('Naptár kiválasztás visszaállítása sikertelen:', err);
          this.cdr.markForCheck();
        },
      });
  }

  // ─── Időrács felbontás mentése/visszaállítása ──────

  private saveSlotDuration(): void {
    this.userPreferencesService.saveCalendarSlotDuration(this.slotDuration)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Időrács felbontás mentése sikertelen:', err),
      });
  }

  private restoreSlotDuration(): void {
    this.userPreferencesService.getCalendarSlotDuration()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.slotDuration = res.slot_duration || '00:15:00';
          // Értesítjük a többi komponenst (pl. floating calendar) a betöltött értékről
          this.userPreferencesService.notifySlotDurationLoaded(this.slotDuration);
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Időrács felbontás visszaállítása sikertelen:', err),
      });
  }
}
